<template>
	<div
		v-if="duty && (duty.id || duty.type)"
		class="duty-cell-item"
		:class="cellData.dutyStyle.classes"
		:style="dynamicComponentStyles"
		:title="cellData.dutyStyle.tooltip"
		:data-type="duty.type"
		:data-duty-id="duty.id"
	>
		<div class="duty-name">{{ truncate(cellData.dutyStyle.name, 5) }}</div>

		<div v-if="cellData.mergedBottomSegments.length" class="bottom-status-bar">
			<div
				v-for="(seg, i) in cellData.mergedBottomSegments"
				:key="i"
				class="segment"
				:class="[seg.type, { 'is-striped': seg.striped }]"
				:style="{ '--seg-color': seg.color }"
			/>
		</div>
	</div>

	<div v-else class="duty-cell-empty" data-type="empty-duty" />
</template>

<script setup lang="ts">
import { toRefs, computed, onBeforeUnmount } from 'vue';
import { useDutyCell } from '../../composables/useDutyStyles';
import { useFormatter } from '../../composables/useFormatter';
import { useDutyStore } from '../../store/useDutyStore';
import { DutyItemResponse } from '../../types/models/duty';

interface SelectedIndicator {
	itemKey: string;
	groupKey: string;
}

const props = defineProps<{
	duty: DutyItemResponse;
	activeIndicators?: SelectedIndicator[];
	embedded?: boolean;
}>();

defineEmits(['dblclick']);

const { duty, activeIndicators } = toRefs(props);
const dutyStore = useDutyStore();
const { truncate } = useFormatter();

const cellData = computed(() => {
	const id = duty.value?.id;
	const type = duty.value?.type;

	if (!id && !type) {
		return { dutyStyle: { classes: {}, name: '', tooltip: '', styles: {} }, topViolationShadow: {}, mergedBottomSegments: [], activeDutyFlags: [] };
	}

	const indicatorSig = (activeIndicators?.value || [])
		.map((i) => `${i.groupKey}:${i.itemKey}`)
		.sort()
		.join('|');

	const useCache = !props.embedded;
	const cacheKey = id ?? `${type}-${duty.value?.name ?? ''}`;

	if (useCache) {
		const cached = dutyStore.getDutyCellState(cacheKey);
		if (cached && cached.indicatorSig === indicatorSig) return cached;
	}

	const { dutyStyle, topViolationShadow, mergedBottomSegments, activeDutyFlags } = useDutyCell(duty, activeIndicators);
	const calculatedResult = {
		dutyStyle: dutyStyle.value,
		topViolationShadow: topViolationShadow.value,
		mergedBottomSegments: mergedBottomSegments.value,
		activeDutyFlags: activeDutyFlags.value,
		indicatorSig,
	};

	if (useCache) dutyStore.setDutyCellState(cacheKey, calculatedResult);
	return calculatedResult;
});

const dynamicComponentStyles = computed(() => ({
	...cellData.value.dutyStyle.styles,
	'--box-shadow': cellData.value.topViolationShadow?.boxShadow || 'none',
	'--bg-color': cellData.value.dutyStyle.styles?.backgroundColor || 'transparent',
	'--text-color': cellData.value.dutyStyle.styles?.color || 'inherit',
}));
</script>
<style scoped>
.duty-cell-item {
	position: relative;
	width: 59px;
	height: 25px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	overflow: hidden;
	user-select: none;
	-webkit-user-select: none;
	border: 1px solid #e5e7eb;
	font-family: 'Inter', sans-serif;

	background-color: var(--bg-color);
	color: var(--text-color);
	box-shadow: var(--box-shadow);
}

/* Bottom Bar Flex Container */
.bottom-status-bar {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 3px;
	display: flex;
}

.segment {
	height: 100%;
	width: 50%;
	background-color: var(--seg-color);
}

.segment.is-striped {
	background-color: #ffffff;
	background-image: repeating-linear-gradient(45deg, var(--seg-color), var(--seg-color) 2px, transparent 2px, transparent 4px);
}

/* 
 * FIX POSITIONING:
 * If an absence segment is the first child in the bar (meaning there are no preferences), 
 * margin-left: auto pushes it cleanly to the right side slot.
 */
.segment.absence:first-child {
	margin-left: auto;
}

/* Duty Icons & Typography */
.duty-name {
	position: relative;
	z-index: 2;
	font-weight: 600;
	font-size: 12px;
	line-height: 16px;
	letter-spacing: 0%;
}

.has-note::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	border-style: solid;
	border-width: 10px 10px 0 0;
	border-color: #fac94e transparent transparent transparent;
	z-index: 1;
}

.fixed::after {
	content: '\F033E';
	position: absolute;
	right: 1px;
	top: 50%;
	transform: translateY(-50%);
	color: #424242;
	font-family: 'Material Design Icons';
	font-size: 8px;
	z-index: 3;
}

.exchange .duty-name::after {
	content: '\F04E2';
	position: absolute;
	right: -15px;
	bottom: 0;
	color: #424242;
	font-family: 'Material Design Icons';
	font-size: 12px;
}

.report-indicator .duty-name::after {
	content: '\F039A';
	position: absolute;
	left: -8px;
	top: 10%;
	color: #424242;
	font-family: 'Material Design Icons';
	font-size: 6px;
	z-index: 2;
}

.modified .duty-name::before {
	content: '\F012F';
	position: absolute;
	right: -8px;
	top: -8px;
	color: #36e5fc;
	display: inline-block;
	font: normal normal normal 24px/1 'Material Design Icons';
	font-size: 5px;
	text-rendering: auto;
	line-height: inherit;
	-webkit-font-smoothing: antialiased;
}
</style>
